import React, { Component, ReactNode } from 'react';

import { ErrorScenarioView } from './v2/errors/ErrorScenarioView';
import { ERROR_SCENARIOS } from './v2/errors/errorScenarios';
import type { ErrorSurfaceAction } from './v2/errors/ErrorSurface';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  // Transferred from sessionStorage in the constructor so render() uses stable
  // React state rather than re-reading storage (which componentDidCatch may have
  // already mutated by the time render fires again).
  reloadAttempted: boolean;
}

// sessionStorage key: set before reloading on a chunk error, read on the next
// boot to initialise reloadAttempted, then immediately cleared from storage.
const CHUNK_RELOAD_KEY = 'eb_chunk_reloaded';

/**
 * ErrorBoundary component to catch and handle React errors gracefully.
 *
 * Without this, uncaught errors in child components (e.g., Layercode SDK
 * initialization failures) cause the entire app to show a white screen.
 *
 * A caught (non-chunk) render error renders the v2 `serverError` error surface
 * (the calm-voice "Something went wrong on our end." page from the design
 * catalogue), with an in-place "Try again" wired to {@link handleRetry} and a
 * Home link — so a thrown render error lands on the same branded surface as the
 * route-driven `/error/500`, not a bespoke card. The surface is rendered in
 * `chrome="wordmark"` mode so the fallback pulls no app-data hooks (nav queries,
 * profile read) that could re-throw while rendering the crash page — a fallback
 * crash at the top-level boundary would have no parent to catch it.
 *
 * Chunk-load errors (stale assets after a deploy) are a distinct, purpose-built
 * experience: they trigger an automatic silent reload, and if that reload
 * doesn't clear it, a dedicated "page couldn't load" card offers a manual
 * refresh. A sessionStorage flag survives the reload and is transferred into
 * component state in the constructor — preventing both infinite loops and the
 * blank-screen race where componentDidCatch clears the key before render can
 * read it.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <ConversationConsole ... />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    // Read the reload-attempt flag once and transfer it into React state, then
    // immediately remove it from storage. All subsequent render/catch logic
    // reads this.state.reloadAttempted — never sessionStorage directly.
    const reloadAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
    if (reloadAttempted) sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    this.state = { hasError: false, reloadAttempted };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);

    if (this.isChunkLoadError() && !this.state.reloadAttempted) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
    }
  }

  /** True when the error is a failed dynamic import/chunk load (e.g. after a deploy). */
  isChunkLoadError = (): boolean => {
    const msg = this.state.error?.message ?? '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Importing a module script failed')
    );
  };

  handleRetry = () => {
    if (this.isChunkLoadError()) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: undefined, reloadAttempted: false });
  };

  /**
   * Actions for the v2 server-error surface: an in-place retry (resets the
   * boundary — no reload/navigation) and a Home link. Injected over the
   * `serverError` scenario's default Back/Home set.
   */
  private readonly errorActions: ErrorSurfaceAction[] = [
    { label: 'Try again', kind: 'primary', icon: 'refresh', onClick: this.handleRetry },
    { label: 'Home', kind: 'ghost', icon: 'home', href: '/' },
  ];

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunk = this.isChunkLoadError();

      // Chunk error, reload not yet attempted: render nothing while reload fires.
      if (isChunk && !this.state.reloadAttempted) {
        return null;
      }

      // Chunk error that persisted after the auto-reload: a dedicated, purpose-
      // built card (the v2 catalogue has no chunk scenario, and this copy is
      // more accurate for a stale-asset/connection failure than "server error").
      if (isChunk) {
        return (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-destructive-muted flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Page couldn&apos;t load
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              This can happen with an unstable connection or after a deploy.
              Refreshing usually fixes it.
            </p>
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
            >
              Refresh page
            </button>
          </div>
        );
      }

      // Non-chunk render crash: the branded v2 server-error surface, with the
      // boundary's in-place retry injected over the scenario's default actions.
      // `chrome="wordmark"` keeps the fallback dependency-free (no GlobalNavBar
      // data hooks / voice profile read) so it can never itself re-throw — a
      // fallback crash at the top-level boundary has no parent to catch it.
      return (
        <ErrorScenarioView
          scenario={ERROR_SCENARIOS.serverError}
          actions={this.errorActions}
          chrome="wordmark"
        />
      );
    }

    return this.props.children;
  }
}
